import React, { useState, useEffect } from 'react';
import { Download, X } from 'lucide-react';
import Panels from '../../../../components/common/Panels/Panels';
import API_ENDPOINTS from '../../../../config/apiEndpoints';
import { getSession } from '../../../../getSession';

const PDFExportOverlay = ({ isOpen, onClose, enquiry, SERVICE_AZURE_URL_DEVE_1, TokenId }) => {
    const { userId, parentId } = getSession();
    const [pdfColumns, setPdfColumns] = useState([]);
    const [selectedPdfColumns, setSelectedPdfColumns] = useState([]);
    const [pdfTitle, setPdfTitle] = useState('Summary');
    const [isLoading, setIsLoading] = useState(false);
    const [pdfAdditionalInfo, setPdfAdditionalInfo] = useState({
        timeStamp: false,
        createdBy: false,
        pageNo: false
    });
    const LOCAL_BASE_URL = "http://localhost:62194/";
    const NEWV3_BASE_URL = (process.env.REACT_APP_SERVICES_API_BASE_URL || '').replace(/\/$/, '');

    const API_BASE = (process.env.REACT_APP_SERVICES_AZURE_BASEURL || '').replace(/\/$/, '');

    useEffect(() => {
        if (isOpen && enquiry) {
            const init = async () => {
                const headers = await fetchHeaders();
                await fetchPreferences(headers);
            };
            init();
        }
    }, [isOpen, enquiry]);

    const fetchHeaders = async () => {
        try {
            const payload = {
                Token: TokenId,
                Details: JSON.stringify({
                    Mode: "GetTableColumns",
                    TpUserId: Number(userId),
                    ParentId: Number(parentId),
                    TableName: "UserLeads",
                    ExcludedColumns: "ID,LeadNo"
                })
            };

            const response = await fetch(`${API_BASE}${API_ENDPOINTS.ENQUIRIES.GET_COLUMN_LIST_FOR_EXPORTSETTING}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            if (response.ok) {
                const result = await response.json();
                if (result.Status === 1 && result.Details?.length > 0) {
                    const columns = result.Details[0].ColumnNames.split(',');
                    setPdfColumns(columns);
                    return columns;
                }
            }
        } catch (error) {
            console.error("Error fetching PDF headers:", error);
        }
        return [];
    };

    const fetchPreferences = async (availableHeaders) => {
        try {
            const payload = {
                Token: TokenId,
                LoggedUserId: userId,
                Details: JSON.stringify({ UserID: Number(userId) })
            };

            const response = await fetch(`${API_BASE}${API_ENDPOINTS.ENQUIRIES.GET_PREFRENCE_COL}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            if (response.ok) {
                const result = await response.json();
                const prefArray = result.Details || [];

                if (prefArray.length > 0) {
                    const prefStr = prefArray[0];
                    const parts = prefStr.split(',');

                    if (parts[0] && !availableHeaders.includes(parts[0])) {
                        setPdfTitle(parts[0]);
                    }

                    setPdfAdditionalInfo({
                        timeStamp: prefStr.includes('TimeStamp'),
                        createdBy: prefStr.includes('Created_By'),
                        pageNo: prefStr.includes('PageNo')
                    });

                    const savedCols = parts.filter(p => availableHeaders.includes(p));
                    setSelectedPdfColumns(savedCols.length > 0 ? savedCols : availableHeaders);
                } else {
                    setSelectedPdfColumns(availableHeaders);
                }
            }
        } catch (error) {
            console.error("Error fetching PDF preferences:", error);
        }
    };

    const handleSaveSetting = async () => {
        setIsLoading(true);
        try {
            let columnsToSave = [...selectedPdfColumns];
            if (pdfAdditionalInfo.timeStamp) columnsToSave.push("TimeStamp");
            if (pdfAdditionalInfo.createdBy) columnsToSave.push("Created_By");
            if (pdfAdditionalInfo.pageNo) columnsToSave.push("PageNo");

            const payload = {
                Token: TokenId,
                LoggedUserId: userId,
                Details: JSON.stringify({
                    PreferenceTitle: pdfTitle,
                    PDFColData: columnsToSave.join(','),
                    UserID: Number(userId)
                })
            };

            const response = await fetch(`${NEWV3_BASE_URL}${API_ENDPOINTS.ENQUIRIES.SAVE_PDF_OVERLAY_SETTING}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            const result = await response.json();
            if (result.Status === 1) {
                alert("Settings saved successfully!");
            } else {
                alert(result.Message || "Failed to save settings");
            }
        } catch (error) {
            console.error("Error saving PDF settings:", error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleDownloadPdf = async () => {
        setIsLoading(true);
        try {
            const columnsToExport = [...selectedPdfColumns];
            if (pdfAdditionalInfo.timeStamp) columnsToExport.push("TimeStamp");
            if (pdfAdditionalInfo.createdBy) columnsToExport.push("Created_By");
            if (pdfAdditionalInfo.pageNo) columnsToExport.push("PageNo");

            const payload = {
                Token: TokenId,
                LoggedUserId: userId,
                Details: JSON.stringify({
                    SelectedColumns: columnsToExport.join(','),
                    EnquiryID: Number(enquiry.EnquiryId),
                    ParentID: Number(parentId || userId),
                    Title: pdfTitle,
                    ExportType: "PDF"
                })
            };

            const response = await fetch(`${API_BASE}${API_ENDPOINTS.ENQUIRIES.SAVE_PDF}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            if (response.ok) {
                const result = await response.json();
                if ((result.Status === 1 || result.Status === undefined) && result.Details) {
                    let base64Data = '';

                    // Case 1: Backend returned a Base64 PDF string
                    if (typeof result.Details === 'string' && (result.Details.length > 1000 || result.Details.startsWith('JVBERi'))) {
                        base64Data = result.Details;
                    }
                    // Case 2: Backend returned a DataTable (JSON array) - Generate PDF on frontend
                    else if (Array.isArray(result.Details) || (typeof result.Details === 'object')) {
                        const data = Array.isArray(result.Details) ? result.Details : [result.Details];
                        await generatePdfFromData(data, columnsToExport);
                        return;
                    }

                    if (!base64Data) throw new Error("No valid PDF data received");

                    base64Data = base64Data.replace(/\s/g, '');
                    if (base64Data.includes(',')) base64Data = base64Data.split(',')[1];

                    const byteCharacters = atob(base64Data);
                    const byteNumbers = new Array(byteCharacters.length);
                    for (let i = 0; i < byteCharacters.length; i++) {
                        byteNumbers[i] = byteCharacters.charCodeAt(i);
                    }
                    const byteArray = new Uint8Array(byteNumbers);
                    const blob = new Blob([byteArray], { type: 'application/pdf' });

                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `${pdfTitle.replace(/\s/g, '_')}_${enquiry.EnquiryId}.pdf`;
                    document.body.appendChild(a);
                    a.click();
                    window.URL.revokeObjectURL(url);
                    document.body.removeChild(a);
                }
            }
        } catch (error) {
            console.error("Error downloading PDF:", error);
            alert("Error generating PDF. Please check selection.");
        } finally {
            setIsLoading(false);
        }
    };

    const generatePdfFromData = async (data, columns) => {
        try {
            // 1. Load jsPDF from CDN if not present
            if (!window.jspdf) {
                await new Promise((resolve, reject) => {
                    const script = document.createElement('script');
                    script.src = 'https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js';
                    script.onload = resolve;
                    script.onerror = () => reject(new Error("Failed to load jsPDF library"));
                    document.head.appendChild(script);
                });
            }

            // 2. Load jsPDF-AutoTable from CDN if not present
            if (!window.jspdf?.jsPDF?.API?.autoTable) {
                await new Promise((resolve, reject) => {
                    const script = document.createElement('script');
                    script.src = 'https://cdnjs.cloudflare.com/ajax/libs/jspdf-autotable/3.5.28/jspdf.plugin.autotable.min.js';
                    script.onload = resolve;
                    script.onerror = () => reject(new Error("Failed to load jsPDF-AutoTable library"));
                    document.head.appendChild(script);
                });
            }

            const { jsPDF } = window.jspdf;
            // Portrait A4 — matches the 3rd/4th reference images
            const doc = new jsPDF('p', 'mm', 'a4');
            const pageWidth = doc.internal.pageSize.getWidth();
            const pageHeight = doc.internal.pageSize.getHeight();
            const margin = 14;
            const usableWidth = pageWidth - margin * 2;

            // --- Helper: format display value ---
            const formatVal = (val) => {
                if (val === null || val === undefined || val === '') return '';
                return String(val);
            };

            // --- For each record in the data array ---
            data.forEach((item, recordIdx) => {
                if (recordIdx > 0) {
                    doc.addPage();
                }

                // ----- Header block -----
                let cursorY = margin;

                // Title (small, blue)
                doc.setFontSize(9);
                doc.setTextColor(59, 130, 246); // blue
                doc.setFont('helvetica', 'bold');
                doc.text(pdfTitle || 'Summary', margin, cursorY);
                cursorY += 5;

                // Created By sub-line (if enabled)
                if (pdfAdditionalInfo.createdBy) {
                    const createdBy = formatVal(item['CreatedBy'] || item['AssignedTo'] || '');
                    doc.setFontSize(8);
                    doc.setTextColor(100, 100, 100);
                    doc.setFont('helvetica', 'normal');
                    doc.text(`Created By ${createdBy}`, margin, cursorY);
                    cursorY += 5;
                }

                // Timestamp line (if enabled)
                if (pdfAdditionalInfo.timeStamp) {
                    doc.setFontSize(8);
                    doc.setTextColor(130, 130, 130);
                    doc.setFont('helvetica', 'normal');
                    doc.text(`Generated: ${new Date().toLocaleString()}`, margin, cursorY);
                    cursorY += 5;
                }

                cursorY += 2; // small gap before table

                // ----- Build Field / Value rows -----
                // Filter to only selected columns (in order)
                const fieldRows = columns
                    .filter(col => !['TimeStamp', 'Created_By', 'PageNo'].includes(col))
                    .map(col => [col, formatVal(item[col])]);

                // Split into pages of max ~30 rows so nothing overflows
                const ROWS_PER_PAGE = 28;
                for (let i = 0; i < fieldRows.length; i += ROWS_PER_PAGE) {
                    if (i > 0) {
                        doc.addPage();
                        cursorY = margin;
                    }

                    const chunk = fieldRows.slice(i, i + ROWS_PER_PAGE);

                    doc.autoTable({
                        head: [['Field', 'Value']],
                        body: chunk,
                        startY: cursorY,
                        tableWidth: usableWidth,
                        margin: { left: margin, right: margin },
                        theme: 'grid',
                        headStyles: {
                            fillColor: [59, 130, 246], // blue header
                            textColor: [255, 255, 255],
                            fontStyle: 'bold',
                            fontSize: 9,
                            cellPadding: 3,
                            halign: 'left'
                        },
                        columnStyles: {
                            0: {
                                // Field name column — light blue text
                                cellWidth: usableWidth * 0.4,
                                fontStyle: 'normal',
                                textColor: [59, 130, 246],
                                fontSize: 8.5,
                                cellPadding: { top: 2.5, bottom: 2.5, left: 3, right: 2 }
                            },
                            1: {
                                // Value column
                                cellWidth: usableWidth * 0.6,
                                fontStyle: 'normal',
                                textColor: [50, 50, 50],
                                fontSize: 8.5,
                                cellPadding: { top: 2.5, bottom: 2.5, left: 3, right: 2 }
                            }
                        },
                        styles: {
                            lineColor: [200, 200, 200],
                            lineWidth: 0.2,
                            overflow: 'linebreak'
                        },
                        alternateRowStyles: {
                            fillColor: [255, 255, 255]
                        },
                        rowPageBreak: 'avoid'
                    });

                    cursorY = doc.lastAutoTable.finalY + 6;

                    // Page number footer (if enabled)
                    if (pdfAdditionalInfo.pageNo) {
                        const totalPages = doc.internal.getNumberOfPages();
                        doc.setFontSize(8);
                        doc.setTextColor(150);
                        doc.text(
                            `Page ${doc.internal.getCurrentPageInfo().pageNumber} of ${totalPages}`,
                            pageWidth - margin,
                            pageHeight - 8,
                            { align: 'right' }
                        );
                    }
                }
            });

            // Save PDF
            doc.save(`${pdfTitle.replace(/\s/g, '_')}_${enquiry.EnquiryId}.pdf`);

        } catch (err) {
            console.error("Frontend PDF Generation Error:", err);
            throw new Error("Could not generate PDF on client side: " + err.message);
        }
    };

    return (
        <Panels
            isOpen={isOpen}
            onClose={onClose}
            title="Enquiry Export"
            width="700px"
            actions={
                <div className="flex items-center gap-6 text-[12px] font-semibold text-gray-500">
                    <span className="text-gray-900 font-bold">Additional Info</span>
                    {['timeStamp', 'createdBy', 'pageNo'].map(key => (
                        <label key={key} className="flex items-center gap-2 cursor-pointer capitalize">
                            <input
                                type="checkbox"
                                className="w-4 h-4 rounded accent-blue-600"
                                checked={pdfAdditionalInfo[key]}
                                onChange={e => setPdfAdditionalInfo(prev => ({ ...prev, [key]: e.target.checked }))}
                            />
                            {key.replace(/([A-Z])/g, ' $1')}
                        </label>
                    ))}
                </div>
            }
            footer={
                <div className="flex justify-between w-full px-2">
                    <button className="px-6 py-1.5 bg-gray-100 text-gray-600 rounded text-sm font-medium" onClick={onClose}>Cancel</button>
                    <button
                        className="px-6 py-1.5 bg-[#00a6e6] text-white rounded text-sm font-bold flex items-center gap-2 hover:bg-[#0088bc]"
                        onClick={handleDownloadPdf}
                        disabled={isLoading}
                    >
                        {isLoading ? <span className="animate-spin mr-2">⏳</span> : <Download size={16} />} PDF
                    </button>
                </div>
            }
        >
            <div className="p-2 flex flex-col gap-4">
                <div className="border border-gray-300 rounded overflow-hidden">
                    <div className="px-4 py-2 bg-gray-50 border-b flex justify-between items-center">
                        <h3 className="text-[13px] font-bold text-gray-700">Select Title & Column</h3>
                        <button className="px-4 py-1 text-[12px] bg-white border rounded hover:bg-gray-50" onClick={handleSaveSetting}>Save Setting</button>
                    </div>

                    <div className="p-4 flex flex-col gap-4">
                        <div className="flex flex-col gap-1.5">
                            <label className="text-[13px] font-medium text-gray-700">Report Title</label>
                            <input
                                type="text"
                                className="w-full px-3 py-2 border rounded text-sm outline-none focus:border-blue-400"
                                value={pdfTitle}
                                onChange={e => setPdfTitle(e.target.value)}
                            />
                        </div>

                        <div className="border border-gray-300 rounded overflow-hidden">
                            <div className="flex bg-[#f5f5f5] border-b text-[11px] font-bold text-gray-500">
                                <div className="flex-1 px-4 py-2.5 uppercase">Column Name</div>
                                <div className="px-6 py-2.5 flex items-center gap-3">
                                    <input
                                        type="checkbox"
                                        className="w-4 h-4 rounded accent-blue-600"
                                        checked={pdfColumns.length > 0 && selectedPdfColumns.length === pdfColumns.length}
                                        onChange={e => setSelectedPdfColumns(e.target.checked ? [...pdfColumns] : [])}
                                    />
                                    <span className="uppercase">Select All</span>
                                </div>
                            </div>

                            <div className="max-h-[350px] overflow-y-auto">
                                <table className="w-full text-xs">
                                    <tbody>
                                        {pdfColumns.map((col, idx) => (
                                            <tr key={idx} className="border-b border-gray-100 hover:bg-blue-50/30">
                                                <td className="px-4 py-2.5 text-gray-600 font-medium">{col}</td>
                                                <td className="px-4 py-2.5 text-right w-[150px]">
                                                    <div className="flex justify-center pr-4">
                                                        <input
                                                            type="checkbox"
                                                            className="w-4 h-4 rounded accent-blue-600"
                                                            checked={selectedPdfColumns.includes(col)}
                                                            onChange={e => {
                                                                setSelectedPdfColumns(prev =>
                                                                    e.target.checked ? [...prev, col] : prev.filter(c => c !== col)
                                                                );
                                                            }}
                                                        />
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Panels>
    );
};

export default PDFExportOverlay;
